import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";

const STEPS = [
  { at: [0.18, 0.34], label: "ONE COMPONENT" },
  { at: [0.42, 0.58], label: "MULTIPLE REQUIREMENTS" },
  { at: [0.66, 0.82], label: "A CLEARER MANUFACTURING CONVERSATION" },
];

// F — Connected manufacturing, with the scroll-linked outline→solid component animation.
export const Connected = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.4"], layoutEffect: false });

  const draw = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const solid = useTransform(scrollYProgress, [0.45, 0.78], [0, 1]);
  const measure = useTransform(scrollYProgress, [0.55, 0.8], [0, 1]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-4, 2]);

  return (
    <section ref={ref} data-testid="connected-section" className="on-dark overflow-hidden bg-[var(--navy-2)] py-24 text-white sm:py-32">
      <div className="wrap grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <SectionHead
            dark
            no="03"
            eyebrow="THE COMPLETE REQUIREMENT"
            title={<>Think beyond <span className="font-serif-i text-[#9db8ff]">the individual part.</span></>}
            intro="The material. The mating features. The assembly. The next production order."
          />
          <Reveal delay={0.2}>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg [text-wrap:pretty]">
              Your component belongs to a bigger picture. Bring that context into the conversation from the start.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg [text-wrap:pretty]">
              PMP supports electronic model-based machining, prototype and production quantities, and sub-assembly and full-assembly work.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <button
              type="button"
              data-testid="connected-discuss-button"
              className="btn btn-light mt-8"
              onClick={() => { const el = document.getElementById("contact"); if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -90 }); else el?.scrollIntoView({ behavior: "smooth" }); }}
            >
              Discuss Your Project
            </button>
          </Reveal>
        </div>

        <div>
          <motion.div style={{ rotate }} className="relative rounded-[0.75rem] border border-white/10 bg-[#0a101c] p-6 sm:p-10">
            <svg viewBox="0 0 420 300" className="w-full" aria-hidden="true">
              {/* measurement grid backdrop */}
              <motion.g style={{ opacity: measure }} stroke="rgba(157,184,255,0.25)" strokeWidth="0.6">
                <line x1="30" y1="268" x2="390" y2="268" />
                <line x1="30" y1="262" x2="30" y2="274" /><line x1="390" y1="262" x2="390" y2="274" />
                <line x1="30" y1="40" x2="30" y2="268" strokeDasharray="3 5" />
              </motion.g>
              {/* outline draws itself */}
              <motion.g style={{ pathLength: draw }} fill="none" stroke="#9db8ff" strokeWidth="1.6" strokeLinecap="round">
                <motion.path d="M120 90 h180 a14 14 0 0 1 14 14 v82 a14 14 0 0 1 -14 14 h-180 a14 14 0 0 1 -14 -14 v-82 a14 14 0 0 1 14 -14 z" />
                <motion.path d="M150 90 v-26 a8 8 0 0 1 8 -8 h104 a8 8 0 0 1 8 8 v26" />
                <motion.path d="M164 150 a46 46 0 0 1 92 0 a46 46 0 0 1 -92 0" />
                <motion.path d="M136 118 h16 M136 166 h16 M268 118 h16 M268 166 h16" />
              </motion.g>
              {/* solid form fills in */}
              <motion.g style={{ opacity: solid }}>
                <rect x="106" y="90" width="208" height="110" rx="14" fill="rgba(47,123,255,0.28)" />
                <path d="M158 90 v-26 a8 8 0 0 1 8 -8 h88 a8 8 0 0 1 8 8 v26 z" fill="rgba(47,123,255,0.4)" />
                <circle cx="210" cy="150" r="46" fill="#0a101c" />
                <circle cx="210" cy="150" r="46" fill="none" stroke="#2f7bff" strokeWidth="2" />
                <circle cx="210" cy="150" r="14" fill="rgba(47,123,255,0.5)" />
                <rect x="106" y="90" width="208" height="110" rx="14" fill="none" stroke="#2f7bff" strokeWidth="2" />
              </motion.g>
              {/* dimension callouts */}
              <motion.g style={{ opacity: measure }} fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#9db8ff">
                <text x="30" y="256">⌀ THROUGH</text>
                <text x="236" y="76">40.00 ±0.01</text>
              </motion.g>
            </svg>
            <p className="mt-4 font-mono text-[9.5px] tracking-[0.16em] text-white/45">ILLUSTRATIVE VISUAL — NOT A REPRESENTATION OF PMP EQUIPMENT</p>
          </motion.div>

          <div className="mt-8 space-y-0">
            {STEPS.map(({ at, label }, i) => {
              return (
                <Step key={label} at={at} scrollYProgress={scrollYProgress} label={label} last={i === STEPS.length - 1} n={i + 1} />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const Step = ({ at, scrollYProgress, label, last, n }) => {
  const op = useTransform(scrollYProgress, at, [0.25, 1]);
  const lineScale = useTransform(scrollYProgress, [at[0], at[1]], [0, 1]);
  return (
    <div className="relative flex gap-5 pb-6">
      <div className="flex flex-col items-center">
        <motion.span style={{ opacity: op }} className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-mono text-[10px] ${last ? "border-[#2f7bff] bg-[#2f7bff] text-white" : "border-white/25 text-white/80"}`}>
          {n}
        </motion.span>
        {!last && <motion.span style={{ scaleY: lineScale }} className="w-px flex-1 origin-top bg-white/20" />}
      </div>
      <motion.p style={{ opacity: op }} className="pt-1.5 font-mono text-[11.5px] tracking-[0.22em] text-white/90">
        {label}
      </motion.p>
    </div>
  );
};
